import React, { useState, ReactNode } from "react";
import "./pageTemplate.css";
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  IconButton,
  CssBaseline,
  useTheme,
  useMediaQuery,
  styled,
  Paper,
  Divider,
  Container,
  Button,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import { logout } from "../utils/apiHelper";

// Define location data structure
interface Location {
  id: string;
  name: string;
  component: ReactNode;
}

// Sample locations - replace with actual locations and components
const LOCATIONS: Location[] = [
  {
    id: "dashboard",
    name: "Dashboard",
    component: <div>Dashboard Content</div>,
  },
  {
    id: "inventory",
    name: "Inventory",
    component: <div>Inventory Content</div>,
  },
  { id: "orders", name: "Orders", component: <div>Orders Content</div> },
  {
    id: "customers",
    name: "Customers",
    component: <div>Customers Content</div>,
  },
  { id: "reports", name: "Reports", component: <div>Reports Content</div> },
  { id: "settings", name: "Settings", component: <div>Settings Content</div> },
];

// Styled components for custom styling
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: "#000000",
  color: "#ffffff",
  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
  zIndex: theme.zIndex.drawer + 1,
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  "&.Mui-selected": {
    backgroundColor: "#f5f5f5",
    "&:hover": {
      backgroundColor: "#e0e0e0",
    },
  },
  "&:hover": {
    backgroundColor: "#f0f0f0",
  },
}));

// Main drawer width
const drawerWidth = 240;

interface PageTemplateProps {
  children?: ReactNode;
}

const PageTemplate: React.FC<PageTemplateProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location>(
    LOCATIONS[0],
  );

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLocationSelect = (location: Location) => {
    setSelectedLocation(location);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawer = (
    <Box
      sx={{ width: drawerWidth, bgcolor: "background.paper", height: "100%" }}
    >
      {isMobile && (
        <DrawerHeader>
          <Typography variant="h6" sx={{ flexGrow: 1, pl: 2 }}>
            Locations
          </Typography>
          <IconButton onClick={handleDrawerToggle}>
            <CloseIcon />
          </IconButton>
        </DrawerHeader>
      )}
      <Divider />
      <List>
        {LOCATIONS.map((location) => (
          <ListItem key={location.id} disablePadding>
            <StyledListItemButton
              selected={selectedLocation.id === location.id}
              onClick={() => handleLocationSelect(location)}
            >
              <ListItemText primary={location.name} />
            </StyledListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <CssBaseline />

      {/* Top AppBar */}
      <StyledAppBar position="fixed">
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            NuLook ERP
          </Typography>
          <Button
            color="inherit"
            startIcon={<LogoutIcon />}
            onClick={() => logout()}
          >
            Logout
          </Button>
        </Toolbar>
      </StyledAppBar>

      {/* Mobile drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              bgcolor: "background.paper",
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Desktop locations bar */}
      {!isMobile && (
        <AppBar
          position="fixed"
          color="default"
          sx={{
            top: 64, // Below the main AppBar
            bgcolor: "background.paper",
            color: "text.primary",
            boxShadow: 1,
            zIndex: theme.zIndex.drawer - 1,
          }}
        >
          <Toolbar>
            {LOCATIONS.map((location) => (
              <Box
                key={location.id}
                sx={{
                  mx: 2,
                  cursor: "pointer",
                  pb: 0.5,
                  borderBottom:
                    selectedLocation.id === location.id
                      ? "2px solid black"
                      : "2px solid transparent",
                }}
                onClick={() => handleLocationSelect(location)}
              >
                <Typography variant="subtitle1">{location.name}</Typography>
              </Box>
            ))}
          </Toolbar>
        </AppBar>
      )}

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,

          width: "100%",
          mt: isMobile ? 8 : 16, // Adjusted for AppBar height
        }}
      >
        <Paper
          elevation={0}
          sx={{ bgcolor: "background.paper", minHeight: "80vh" }}
        >
          {/* Render the selected location's component */}
          {selectedLocation.component}

          {/* Or render children if provided */}
          {children}
        </Paper>
      </Box>
    </Box>
  );
};

export default PageTemplate;
